import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Cloud, CloudFog, CloudRain, CloudSun, Moon, Sun } from "lucide-react";
import { axiosWeather } from "../../api/axios";
import "./NavbarDateWeather.css";

const TUNIS_COORDINATES = { latitude: 36.8065, longitude: 10.1815 };

const getWeatherIcon = (code, isDay) => {
  if (code === 0) return isDay ? Sun : Moon;
  if ([1, 2, 3].includes(code)) return CloudSun;
  if ([45, 48].includes(code)) return CloudFog;
  if ([51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82, 95, 96, 99].includes(code)) return CloudRain;
  return Cloud;
};

export default function NavbarDateWeather() {
  const { i18n } = useTranslation();
  const [now, setNow] = useState(() => new Date());
  const [weather, setWeather] = useState({ temperature: null, code: null, isDay: true });
  const language = i18n.resolvedLanguage || i18n.language || "en";
  const city = language.startsWith("ar") ? "تونس" : "Tunis";
  const WeatherIcon = getWeatherIcon(weather.code, weather.isDay);
  const dateLabel = new Intl.DateTimeFormat(language, {
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(now);
  const timeLabel = new Intl.DateTimeFormat(language, {
    hour: "2-digit",
    minute: "2-digit",
  }).format(now);

  useEffect(() => {
    const timerId = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(timerId);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const loadWeather = () => axiosWeather.get("/forecast", {
      params: {
        ...TUNIS_COORDINATES,
        current: "temperature_2m,weather_code,is_day",
        timezone: "Africa/Tunis",
        forecast_days: 1,
      },
      signal: controller.signal,
    }).then(({ data }) => {
      const current = data?.current;
      if (!current || !Number.isFinite(Number(current.temperature_2m))) return;
      setWeather({
        temperature: Math.round(Number(current.temperature_2m)),
        code: Number(current.weather_code),
        isDay: Number(current.is_day) === 1,
      });
    }).catch(() => {});

    loadWeather();
    const refreshId = window.setInterval(loadWeather, 15 * 60 * 1000);
    return () => {
      controller.abort();
      window.clearInterval(refreshId);
    };
  }, []);

  return (
    <div className="navbar-date-weather" aria-label={`${dateLabel}, ${timeLabel}, ${weather.temperature ?? "--"}°, ${city}`}>
      <div className="navbar-date-weather__date-time">
        <time dateTime={now.toISOString()}>{dateLabel}</time>
        <time>{timeLabel}</time>
      </div>
      <span className="navbar-date-weather__divider" aria-hidden="true" />
      <div className="navbar-date-weather__weather" title={city}>
        <WeatherIcon size={18} aria-hidden="true" />
        <span className="navbar-date-weather__reading">
          <strong>{weather.temperature ?? "--"}°</strong>
          <span>{city}</span>
        </span>
      </div>
    </div>
  );
}
